import {
  MAX_AREAS_EXTRA,
  awardForFocus,
  awardForHabit,
  awardForJournal,
  awardForMood,
  dayPlanId,
  eventId,
  mutationId,
  habitEntryId,
  journalEntryId,
  moodLogId,
  reviewSessionId,
  weekPlanId,
  sealedSeasonId,
  coleccionDe,
  PROPS_POR_COLECCION,
  PROPS_SOLO_DEL_CABLE,
  type Cadence,
  type HabitStatus,
  type TipoMutacion,
} from '@selflevel/domain';
import {
  db,
  type FocusSession,
  type Goal,
  type GoalMilestone,
  type Habit,
  type HabitEntry,
  type IdentityStatement,
  type JournalEntry,
  type LifeArea,
  type MoodLog,
  type ReviewSession,
  type SealedSeasonRow,
  type Settings,
  type DayPlan,
  type Task,
  type WeekPlan,
} from './db';
import {
  getSettings,
  getUserId,
  isClockPlausible,
  nextHlc,
  today,
  zonaDelDispositivo,
} from './session';

/**
 * TODAS LAS ESCRITURAS PASAN POR AQUÍ.
 *
 * Dos reglas, y las dos son del PRD:
 *
 *  1. **Nunca se sincroniza un interruptor, se sincroniza un estado absoluto.**
 *     `toggle(hábito, fecha)` no es idempotente ni conmutativo: aplicarlo dos
 *     veces deshace lo que hizo. `setStatus(hábito, fecha, 'hecho')` es las dos
 *     cosas. La interfaz puede seguir siendo un interruptor —debe serlo—; la
 *     mutación que viaja, jamás.
 *
 *  2. **La escritura local y el encolado van en la misma transacción.** Si se
 *     guardara primero y se encolara después, un cierre de pestaña entre las dos
 *     dejaría un cambio que el servidor no vería nunca, y el usuario no tendría
 *     forma de saberlo.
 */

/**
 * Los tipos los manda `@selflevel/domain`, no una lista local.
 *
 * Antes había una copia aquí, y una copia de una lista de nombres es una lista que
 * un día dirá algo distinto de la del servidor. El fallo que produce no es un
 * error: es una mutación que el servidor no reconoce y que se queda en la cola
 * hasta morir, con el dato del usuario dentro.
 */
type MutationType = TipoMutacion;

let seqCounter = 0;

async function nextSeq(): Promise<number> {
  if (seqCounter === 0) {
    const last = await db.outbox.orderBy('seq').last();
    seqCounter = last?.seq ?? 0;
  }
  seqCounter += 1;
  return seqCounter;
}

/**
 * SIN CUENTA NO HAY OUTBOX.
 *
 * La cola existe para llevar las escrituras a un servidor. Mientras el usuario
 * no tenga cuenta no hay servidor al que llevarlas, y encolarlas igual haría
 * crecer IndexedDB sin límite con filas que nadie va a leer nunca — un usuario
 * de un año con 4.000 escrituras acumularía 4.000 mutaciones muertas.
 *
 * No se pierde nada: el dato ya está guardado en su tabla, que es la fuente. Al
 * crear la cuenta, el primer envío es un `bootstrap` completo desde las tablas,
 * no una reproducción de la cola.
 */
async function syncEnabled(): Promise<boolean> {
  const marca = await db.meta.get('syncAccountId');
  return typeof marca?.value === 'string' && marca.value.length > 0;
}

/**
 * Encola una mutación. Se llama SIEMPRE dentro de la transacción de la escritura.
 *
 * Dos parámetros y no uno: la **fila completa** porque el servidor necesita todas
 * las columnas obligatorias para poder insertar, y los **campos cambiados** porque
 * son los que compiten en el LWW por campo. Mandar la fila y declarar que cambió
 * todo es lo que parece más simple y lo que hace que dos dispositivos que tocaron
 * campos distintos pierdan uno de los dos cambios.
 */
export async function enqueue(
  type: MutationType,
  row: Record<string, unknown>,
  fields: readonly string[],
): Promise<void> {
  if (!(await syncEnabled())) return;

  const fila = sinColumnasDeControl(aFilaDelCable(type, row));
  comprobarForma(type, fila);

  await db.outbox.add({
    id: mutationId(),
    seq: await nextSeq(),
    type,
    row: fila,
    fields: [...fields],
    hlc: await nextHlc(),
    createdAt: new Date().toISOString(),
    attempts: 0,
    nextAttemptMs: 0,
    lastError: null,
    state: 'pending',
  });
}

/**
 * Encola una fila existente tal cual, declarando que cambió todo.
 *
 * Es lo que usa la vinculación de cuenta para barrer las tablas. Declarar todos
 * los campos es seguro porque el HLC es el de cuando se escribió la fila: si el
 * servidor tiene algo más nuevo, gana lo suyo.
 */
export async function encolarFila(
  type: string,
  row: Record<string, unknown>,
  hlc: string,
): Promise<void> {
  const fila = aFilaDelCable(type as MutationType, row);
  await db.outbox.add({
    id: mutationId(),
    seq: await nextSeq(),
    type,
    row: fila,
    fields: [],
    hlc,
    createdAt: new Date().toISOString(),
    attempts: 0,
    nextAttemptMs: 0,
    lastError: null,
    state: 'pending',
  });
}

/**
 * La temporada sellada es la única fila cuyo nombre de columna difiere entre el
 * almacén local y el servidor: aquí se llama `key`/`from`/`to` porque la interfaz
 * la busca por su clave, y allí `season_key`/`from_date`/`to_date` porque `from` y
 * `to` son palabras reservadas de SQL. La traducción vive aquí y en `sync.ts`, que
 * son los dos únicos sitios que cruzan la frontera.
 */
/**
 * En desarrollo, comprueba que **toda propiedad que se encola es una columna del
 * servidor**.
 *
 * Existe por un fallo que costó 185 mutaciones retiradas: el almacén local llamaba
 * `target` a un campo que el servidor tenía como `target_per_period`, y cada
 * `habit.upsert` se rechazaba entero. No lo vio el compilador ni ningún test, porque el
 * cable va por `Record<string, unknown>`.
 *
 * Falla aquí, al escribir, y no tres semanas después en la cola de un usuario. En
 * producción se elimina entero del bundle con el `import.meta.env.DEV`.
 */
function comprobarForma(type: MutationType, row: Record<string, unknown>): void {
  if (!import.meta.env.DEV) return;

  const coleccion = coleccionDe(type);
  const permitidas = new Set([
    ...(PROPS_POR_COLECCION[coleccion] ?? []),
    ...(PROPS_SOLO_DEL_CABLE[coleccion] ?? []),
    // El almacén local lleva su propio `updatedAt`; el servidor lo pone él y lo ignora.
    'updatedAt',
  ]);

  const sobran = Object.keys(row).filter((prop) => !permitidas.has(prop));
  if (sobran.length > 0) {
    throw new Error(
      `${type}: «${sobran.join('», «')}» no ${sobran.length === 1 ? 'es columna' : 'son columnas'} de ${coleccion}. ` +
        'Añádelas a PROPS_POR_COLECCION y al esquema, o quítalas de la fila que se encola.',
    );
  }
}

/**
 * Las columnas que pone y controla el servidor. Llegan en las filas del pull y del
 * push, y se quedan en el almacén local; al volver a encolar esa fila no tienen que
 * viajar de vuelta (el servidor las ignora, y la comprobación de forma, con razón,
 * las rechaza).
 */
const COLUMNAS_DE_CONTROL = ['deviceId', 'userId', 'hlc', 'hlcMax'] as const;

function sinColumnasDeControl(row: Record<string, unknown>): Record<string, unknown> {
  const limpia = { ...row };
  for (const columna of COLUMNAS_DE_CONTROL) delete limpia[columna];
  return limpia;
}

function aFilaDelCable(type: MutationType, row: Record<string, unknown>): Record<string, unknown> {
  if (type !== 'season.seal') return row;
  const { key, from, to, ...resto } = row;
  return { ...resto, id: row['id'] ?? key, seasonKey: key, fromDate: from, toDate: to };
}

export const nowIso = () => new Date().toISOString();

/** Campos mutables de un hábito. Se nombran una vez y no se repiten por el fichero. */
const CAMPOS_HABITO = [
  'areaId',
  'description',
  'extraAreaIds',
  'identityId',
  'goalId',
  'name',
  'marca',
  'cadence',
  'target',
  'unit',
  'reminderTime',
  'sortOrder',
  'archivedAt',
] as const;

const CAMPOS_META = [
  'areaId',
  'identityId',
  'title',
  'horizon',
  'targetDate',
  'status',
  'completedAt',
] as const;

// ===========================================================================
// HÁBITOS
// ===========================================================================

export async function setHabitStatus(args: {
  habitId: string;
  localDate: string;
  status: HabitStatus;
  value?: number | null;
  tz: string;
}): Promise<void> {
  // El id es determinista: dos dispositivos sin cobertura marcando lo mismo el
  // mismo día producen UNA fila, no dos. Si la fila ya existe —bajada de otro
  // dispositivo, o escrita antes de vincular la cuenta— se reutiliza su id.
  const existente = await db.habitEntries
    .where('[habitId+localDate]')
    .equals([args.habitId, args.localDate])
    .first();
  const id = existente?.id ?? habitEntryId(getUserId(), args.habitId, args.localDate);
  const attested = isClockPlausible();

  const entry: HabitEntry = {
    id,
    habitId: args.habitId,
    localDate: args.localDate,
    status: args.status,
    value: args.value ?? null,
    // La zona en la que está el dispositivo al marcar, no la del alta: es la que
    // deja ver al perdón del viaje (PRD 7.4) que la zona cambió.
    tz: zonaDelDispositivo(args.tz),
    clientTs: nowIso(),
    attested,
    // Quien marca a mano es el dueño del día: si antes lo marcó otra app, deja de
    // decir «desde Gimnasio». Cuenta quien lo tocó por última vez.
    source: 'propio',
    updatedAt: nowIso(),
    deletedAt: null,
  };

  await db.transaction('rw', db.habitEntries, db.outbox, db.meta, async () => {
    await db.habitEntries.put(entry);
    // La fila entera, pero declarando solo lo que esta pulsación cambió: si otro
    // dispositivo anotó el valor y este el estado, los dos ganan en su campo.
    await enqueue('habit.setStatus', entry, [
      'status',
      'value',
      'tz',
      'clientTs',
      'attested',
      'source',
    ]);
  });
}

export async function upsertHabit(habit: {
  id?: string;
  areaId: string;
  identityId?: string | null;
  goalId?: string | null;
  name: string;
  marca: string;
  cadence: Cadence;
  target?: number | null;
  unit?: string | null;
  reminderTime?: string | null;
  /** Opcional; vacía la quita. Hasta 140 caracteres. */
  description?: string | null;
  /** Otras áreas que trabaja (como mucho `MAX_AREAS_EXTRA`, nunca la principal). */
  extraAreaIds?: readonly string[];
}): Promise<string> {
  const id = habit.id ?? eventId();
  const existing = habit.id ? await db.habits.get(habit.id) : undefined;
  const count = await db.habits.count();

  // `undefined` es «el formulario no lo toca» y conserva lo que había; `null` es
  // «quitarlo». El editor no manda identidad, meta ni recordatorio, y antes cada
  // edición los borraba —y declaraba borrados en los otros dispositivos—.
  const conservar = <T>(nuevo: T | null | undefined, anterior: T | null | undefined): T | null =>
    nuevo === undefined ? (anterior ?? null) : nuevo;

  const row: Habit = {
    id,
    areaId: habit.areaId,
    identityId: conservar(habit.identityId, existing?.identityId),
    goalId: conservar(habit.goalId, existing?.goalId),
    name: habit.name.trim(),
    marca: habit.marca,
    description:
      habit.description === undefined
        ? (existing?.description ?? null)
        : habit.description?.trim().slice(0, 140) || null,
    extraAreaIds: [...new Set(habit.extraAreaIds ?? existing?.extraAreaIds ?? [])]
      .filter((a) => a !== habit.areaId)
      .slice(0, MAX_AREAS_EXTRA),
    cadence: habit.cadence,
    target: conservar(habit.target, existing?.target),
    unit: conservar(habit.unit, existing?.unit),
    reminderTime: conservar(habit.reminderTime, existing?.reminderTime),
    sortOrder: existing?.sortOrder ?? count,
    archivedAt: existing?.archivedAt ?? null,
    createdAt: existing?.createdAt ?? nowIso(),
  };

  // Solo se declara lo que cambió: declarar un campo intacto lo hace competir en
  // el LWW y puede pisar lo que otro dispositivo cambió de verdad.
  const cambiados = existing
    ? CAMPOS_HABITO.filter(
        (campo) => JSON.stringify(row[campo]) !== JSON.stringify(existing[campo]),
      )
    : CAMPOS_HABITO;
  if (existing && cambiados.length === 0) return id;

  await db.transaction('rw', db.habits, db.outbox, db.meta, async () => {
    await db.habits.put(row);
    await enqueue('habit.upsert', row, cambiados);
  });
  return id;
}

/**
 * Archivar, no borrar. Un hábito que se abandona sigue siendo parte del año del
 * usuario: el rewind lo cuenta, y el mapa de calor lo enseña. Borrarlo
 * reescribiría su historia.
 */
export async function archiveHabit(id: string, archived: boolean): Promise<void> {
  const at = archived ? nowIso() : null;
  await db.transaction('rw', db.habits, db.outbox, db.meta, async () => {
    await db.habits.update(id, { archivedAt: at });
    const fila = await db.habits.get(id);
    // Archivar es un `habit.upsert` que solo declara `archivedAt`. No hace falta un
    // tipo propio, y tenerlo sería un segundo camino de escritura a la misma tabla.
    if (fila) await enqueue('habit.upsert', fila, ['archivedAt']);
  });
}

/**
 * Archivar o recuperar un ÁREA, y sus hábitos con ella.
 *
 * No existía: `upsertArea` conservaba `archivedAt` pero nadie lo cambiaba, así
 * que un área elegida en el alta era para siempre.
 *
 * LOS HÁBITOS CAEN CON EL ÁREA, y con el MISMO instante. Un área archivada con
 * sus hábitos vivos dejaría en Hoy casillas de algo que ya no está en el dial ni
 * en ninguna lista. Y el instante compartido es lo que hace exacto el camino de
 * vuelta: al recuperar el área se recuperan SOLO los hábitos que cayeron con
 * ella, no los que el usuario ya había archivado antes por su cuenta —esos
 * siguen archivados, porque esa decisión fue suya y era otra—.
 *
 * Nada se borra: el historial sigue contando en el mapa de calor, en las
 * temporadas y en el rewind. Lo que hiciste, lo hiciste.
 */
export async function archiveArea(id: string, archived: boolean): Promise<void> {
  await db.transaction('rw', db.lifeAreas, db.habits, db.outbox, db.meta, async () => {
    const area = await db.lifeAreas.get(id);
    if (!area) return;

    const at = archived ? nowIso() : null;
    const antes = area.archivedAt;
    await db.lifeAreas.update(id, { archivedAt: at });
    const fila = await db.lifeAreas.get(id);
    if (fila) await enqueue('area.upsert', fila, ['archivedAt']);

    const suyos = await db.habits.where('areaId').equals(id).toArray();
    for (const habito of suyos) {
      const caen = archived && habito.archivedAt === null;
      const vuelven = !archived && antes !== null && habito.archivedAt === antes;
      if (!caen && !vuelven) continue;
      await db.habits.update(habito.id, { archivedAt: at });
      const h = await db.habits.get(habito.id);
      if (h) await enqueue('habit.upsert', h, ['archivedAt']);
    }
  });
}

export async function reorderHabits(ids: readonly string[]): Promise<void> {
  await db.transaction('rw', db.habits, db.outbox, db.meta, async () => {
    for (const [index, id] of ids.entries()) {
      await db.habits.update(id, { sortOrder: index });
      const fila = await db.habits.get(id);
      /**
       * Una mutación por hábito, no una con la lista. Reordenar es N escrituras
       * absolutas —«este hábito va en la posición 3»— y no una operación sobre el
       * conjunto: si viajara la lista, dos dispositivos reordenando a la vez
       * producirían un orden que ninguno de los dos pidió.
       */
      if (fila) await enqueue('habit.upsert', fila, ['sortOrder']);
    }
  });
}

// ===========================================================================
// ÁREAS, IDENTIDADES Y METAS
// ===========================================================================

export async function upsertArea(area: {
  id?: string;
  name: string;
  marca: string;
  colorToken: string;
}): Promise<string> {
  const id = area.id ?? eventId();
  const existing = area.id ? await db.lifeAreas.get(area.id) : undefined;
  const count = await db.lifeAreas.count();

  const row: LifeArea = {
    id,
    name: area.name.trim(),
    marca: area.marca,
    colorToken: area.colorToken,
    sortOrder: existing?.sortOrder ?? count,
    archivedAt: existing?.archivedAt ?? null,
  };

  await db.transaction('rw', db.lifeAreas, db.outbox, db.meta, async () => {
    await db.lifeAreas.put(row);
    await enqueue('area.upsert', row, ['name', 'marca', 'colorToken', 'sortOrder', 'archivedAt']);
  });
  return id;
}

export async function upsertIdentity(identity: {
  id?: string;
  areaId: string;
  text: string;
}): Promise<string> {
  const id = identity.id ?? eventId();
  const existing = identity.id ? await db.identities.get(identity.id) : undefined;

  const row: IdentityStatement = {
    id,
    areaId: identity.areaId,
    text: identity.text.trim(),
    adoptedAt: existing?.adoptedAt ?? nowIso(),
    retiredAt: existing?.retiredAt ?? null,
  };

  await db.transaction('rw', db.identities, db.outbox, db.meta, async () => {
    await db.identities.put(row);
    await enqueue('identity.upsert', row, ['areaId', 'text', 'retiredAt']);
  });
  return id;
}

export async function upsertGoal(goal: {
  id?: string;
  areaId: string;
  identityId?: string | null;
  title: string;
  horizon: Goal['horizon'];
  targetDate?: string | null;
}): Promise<string> {
  const id = goal.id ?? eventId();
  const existing = goal.id ? await db.goals.get(goal.id) : undefined;

  const row: Goal = {
    id,
    areaId: goal.areaId,
    identityId: goal.identityId ?? null,
    title: goal.title.trim(),
    horizon: goal.horizon,
    targetDate: goal.targetDate ?? null,
    status: existing?.status ?? 'activa',
    completedAt: existing?.completedAt ?? null,
    createdAt: existing?.createdAt ?? nowIso(),
  };

  await db.transaction('rw', db.goals, db.outbox, db.meta, async () => {
    await db.goals.put(row);
    await enqueue('goal.upsert', row, CAMPOS_META);
  });
  return id;
}

export async function setGoalStatus(id: string, status: Goal['status']): Promise<void> {
  const completedAt = status === 'cumplida' ? nowIso() : null;
  // El día lógico es el que puntúa (v5): se pone al cumplirla y se quita al reabrirla.
  const localDate = status === 'cumplida' ? today(await getSettings()) : null;
  await db.transaction('rw', db.goals, db.outbox, db.meta, async () => {
    await db.goals.update(id, { status, completedAt, localDate });
    const fila = await db.goals.get(id);
    if (fila) await enqueue('goal.upsert', fila, ['status', 'completedAt', 'localDate']);
  });
}

export async function upsertGoalMilestone(milestone: {
  id?: string;
  goalId: string;
  title: string;
}): Promise<string> {
  const id = milestone.id ?? eventId();
  const count = await db.goalMilestones.where('goalId').equals(milestone.goalId).count();
  const existing = milestone.id ? await db.goalMilestones.get(milestone.id) : undefined;

  const row: GoalMilestone = {
    id,
    goalId: milestone.goalId,
    title: milestone.title.trim(),
    doneAt: existing?.doneAt ?? null,
    sortOrder: existing?.sortOrder ?? count,
  };

  await db.transaction('rw', db.goalMilestones, db.outbox, db.meta, async () => {
    await db.goalMilestones.put(row);
    await enqueue('goalMilestone.upsert', row, ['goalId', 'title', 'doneAt', 'sortOrder']);
  });
  return id;
}

export async function setGoalMilestoneDone(id: string, done: boolean): Promise<void> {
  const doneAt = done ? nowIso() : null;
  // Marcar da sus 50 una vez (el id del evento es el del sub-hito); desmarcar los quita.
  const localDate = done ? today(await getSettings()) : null;
  await db.transaction('rw', db.goalMilestones, db.outbox, db.meta, async () => {
    await db.goalMilestones.update(id, { doneAt, localDate });
    const fila = await db.goalMilestones.get(id);
    if (fila) await enqueue('goalMilestone.upsert', fila, ['doneAt', 'localDate']);
  });
}

// ===========================================================================
// FOCO · append-only
// ===========================================================================

export async function recordFocusSession(session: {
  habitId?: string | null;
  areaId?: string | null;
  label: string;
  localDate: string;
  startedAt: string;
  plannedMinutes: number;
  actualMinutes: number;
  interrupted: boolean;
  tz: string;
}): Promise<string> {
  const id = eventId();
  const row: FocusSession = {
    id,
    habitId: session.habitId ?? null,
    areaId: session.areaId ?? null,
    label: session.label.trim(),
    localDate: session.localDate,
    startedAt: session.startedAt,
    endedAt: nowIso(),
    plannedMinutes: session.plannedMinutes,
    actualMinutes: session.actualMinutes,
    interrupted: session.interrupted,
    supersedesId: null,
    tz: session.tz,
    attested: isClockPlausible(),
  };

  await db.transaction('rw', db.focusSessions, db.outbox, db.meta, async () => {
    await db.focusSessions.add(row);
    // Append-only: se crea y no se vuelve a tocar, así que declara todo.
    await enqueue('focus.record', row, []);
  });
  return id;
}

// ===========================================================================
// DIARIO Y ÁNIMO
// ===========================================================================

export async function saveJournal(entry: {
  localDate: string;
  body: string;
  tags?: string[];
  tz: string;
}): Promise<void> {
  const existente = await db.journalEntries.where('localDate').equals(entry.localDate).first();
  const id = existente?.id ?? journalEntryId(getUserId(), entry.localDate);
  const ajustes = await db.settings.get('unica');
  const privado = ajustes?.journalPrivateMode === true;
  const row: JournalEntry = {
    id,
    localDate: entry.localDate,
    body: entry.body,
    tags: entry.tags ?? [],
    tz: entry.tz,
    updatedAt: nowIso(),
    deletedAt: null,
  };

  await db.transaction('rw', db.journalEntries, db.outbox, db.meta, async () => {
    if (entry.body.trim() === '') {
      await db.journalEntries.delete(id);
    } else {
      await db.journalEntries.put(row);
    }
    /**
     * El cuerpo viaja **en claro por TLS** y lo cifra la API antes de guardarlo
     * (PRD 16.4). No es cifrado de extremo a extremo y no se va a llamar así: la
     * clave vive en el contenedor de la API, así que el operador podría leerlo. Lo
     * que protege —y protege de verdad— es el volcado del volumen y el backup
     * filtrado, que son los escenarios realistas.
     *
     * Se eligió así porque E2EE real es incompatible con el copiloto: si el
     * servidor no puede leer el diario, la IA tampoco. Para quien prefiera eso está
     * el modo privado, donde cifra este dispositivo con su frase de paso y el
     * servidor guarda un blob que no puede abrir.
     */
    await enqueue('journal.save', { ...row, privateMode: privado }, ['body', 'tags']);
  });
}

export async function recordMood(log: {
  localDate: string;
  mood: number;
  energy: number;
  note?: string;
  tz: string;
}): Promise<void> {
  const existente = await db.moodLogs.where('localDate').equals(log.localDate).first();
  const id = existente?.id ?? moodLogId(getUserId(), log.localDate);
  const row: MoodLog = {
    id,
    localDate: log.localDate,
    mood: log.mood,
    energy: log.energy,
    note: log.note ?? '',
    notedAt: nowIso(),
    tz: log.tz,
    attested: isClockPlausible(),
  };

  await db.transaction('rw', db.moodLogs, db.outbox, db.meta, async () => {
    await db.moodLogs.put(row);
    await enqueue('mood.record', row, ['mood', 'energy', 'note', 'notedAt']);
  });
}

// ===========================================================================
// REVISIÓN
// ===========================================================================

export async function saveReview(review: {
  periodType: 'weekly' | 'monthly';
  periodKey: string;
  answers: Record<string, string>;
  localDate: string;
  complete: boolean;
}): Promise<string> {
  const previa = await db.reviews
    .where('[periodType+periodKey]')
    .equals([review.periodType, review.periodKey])
    .first();
  const id = previa?.id ?? reviewSessionId(getUserId(), review.periodType, review.periodKey);
  const existing = await db.reviews.get(id);

  const row: ReviewSession = {
    id,
    periodType: review.periodType,
    periodKey: review.periodKey,
    answers: review.answers,
    localDate: review.localDate,
    completedAt: review.complete ? (existing?.completedAt ?? nowIso()) : null,
    attested: isClockPlausible(),
  };

  await db.transaction('rw', db.reviews, db.outbox, db.meta, async () => {
    await db.reviews.put(row);
    await enqueue('review.save', row, ['answers', 'completedAt']);
  });
  return id;
}

export async function addReviewAction(reviewId: string, text: string): Promise<void> {
  const id = eventId();
  const fila = { id, reviewId, text: text.trim(), doneAt: null };
  await db.transaction('rw', db.reviewActions, db.outbox, db.meta, async () => {
    await db.reviewActions.add(fila);
    await enqueue('reviewAction.save', fila, []);
  });
}

export async function setReviewActionDone(id: string, done: boolean): Promise<void> {
  const doneAt = done ? nowIso() : null;
  await db.transaction('rw', db.reviewActions, db.outbox, db.meta, async () => {
    await db.reviewActions.update(id, { doneAt });
    const fila = await db.reviewActions.get(id);
    if (fila) await enqueue('reviewAction.save', fila, ['doneAt']);
  });
}

// ===========================================================================
// HOY CON INTENCIÓN · tareas, plan del día y semana
// ===========================================================================

const CAMPOS_TAREA = [
  'title',
  'plannedDate',
  'areaId',
  'goalId',
  'doneAt',
  'doneLocalDate',
  'sortOrder',
  'attested',
  'deletedAt',
] as const;

/** Cuántas tareas vivas hay ya en ese día (o en la bandeja): la nueva va al final. */
async function siguienteOrden(plannedDate: string | null): Promise<number> {
  const enElDia =
    plannedDate === null
      ? await db.tasks.filter((t) => t.plannedDate === null).toArray()
      : await db.tasks.where('plannedDate').equals(plannedDate).toArray();
  return enElDia.filter((t) => !t.deletedAt).length;
}

export async function addTask(t: {
  title: string;
  plannedDate: string | null;
  areaId?: string | null;
  goalId?: string | null;
  tz: string;
}): Promise<string> {
  const id = eventId();
  const row: Task = {
    id,
    title: t.title.trim().slice(0, 140),
    plannedDate: t.plannedDate,
    areaId: t.areaId ?? null,
    goalId: t.goalId ?? null,
    doneAt: null,
    doneLocalDate: null,
    sortOrder: await siguienteOrden(t.plannedDate),
    tz: t.tz,
    attested: isClockPlausible(),
    createdAt: nowIso(),
    deletedAt: null,
  };
  await db.transaction('rw', db.tasks, db.outbox, db.meta, async () => {
    await db.tasks.add(row);
    await enqueue('task.upsert', row, CAMPOS_TAREA);
  });
  return id;
}

/** Estado absoluto, nunca un interruptor: tocar dos veces «hecha» deja «hecha». */
export async function setTaskDone(id: string, done: boolean, today: string): Promise<void> {
  const patch = done
    ? { doneAt: nowIso(), doneLocalDate: today, attested: isClockPlausible() }
    : { doneAt: null, doneLocalDate: null };
  await db.transaction('rw', db.tasks, db.outbox, db.meta, async () => {
    await db.tasks.update(id, patch);
    const fila = await db.tasks.get(id);
    if (fila) await enqueue('task.upsert', fila, Object.keys(patch));
  });
}

export async function updateTask(
  id: string,
  patch: Partial<Pick<Task, 'title' | 'plannedDate' | 'areaId' | 'goalId'>>,
): Promise<void> {
  const actual = await db.tasks.get(id);
  if (!actual) return;
  const cambio: Partial<Task> = { ...patch };
  if (patch.title !== undefined) cambio.title = patch.title.trim().slice(0, 140);
  if (patch.plannedDate !== undefined && patch.plannedDate !== actual.plannedDate) {
    cambio.sortOrder = await siguienteOrden(patch.plannedDate);
  }
  await db.transaction('rw', db.tasks, db.outbox, db.meta, async () => {
    await db.tasks.update(id, cambio);
    const fila = await db.tasks.get(id);
    if (fila) await enqueue('task.upsert', fila, Object.keys(cambio));
  });
}

/** Lápida, nunca un borrado físico (PRD 6.10). */
export async function deleteTask(id: string): Promise<void> {
  await db.transaction('rw', db.tasks, db.outbox, db.meta, async () => {
    await db.tasks.update(id, { deletedAt: nowIso() });
    const fila = await db.tasks.get(id);
    if (fila) await enqueue('task.upsert', fila, ['deletedAt']);
  });
}

async function planDelDia(localDate: string, tz: string): Promise<DayPlan> {
  const existente = await db.dayPlans.where('localDate').equals(localDate).first();
  return (
    existente ?? {
      id: dayPlanId(getUserId(), localDate),
      localDate,
      mission: '',
      pillarAreaId: null,
      frogTaskId: null,
      projectGoalIds: [],
      ritualSealedAt: null,
      tz,
      attested: isClockPlausible(),
      deletedAt: null,
    }
  );
}

export async function saveDayPlan(
  localDate: string,
  tz: string,
  patch: Partial<Pick<DayPlan, 'mission' | 'pillarAreaId' | 'frogTaskId' | 'projectGoalIds'>>,
): Promise<void> {
  await db.transaction('rw', db.dayPlans, db.outbox, db.meta, async () => {
    const plan = await planDelDia(localDate, tz);
    const row: DayPlan = {
      ...plan,
      ...patch,
      mission: (patch.mission ?? plan.mission).slice(0, 120),
      projectGoalIds: (patch.projectGoalIds ?? plan.projectGoalIds).slice(0, 3),
    };
    await db.dayPlans.put(row);
    await enqueue('dayPlan.save', row, Object.keys(patch));
  });
}

/** Sellar dos veces no cambia la hora del primer sellado. */
export async function sealRitual(localDate: string, tz: string): Promise<void> {
  await db.transaction('rw', db.dayPlans, db.outbox, db.meta, async () => {
    const plan = await planDelDia(localDate, tz);
    if (plan.ritualSealedAt) return;
    const row: DayPlan = { ...plan, ritualSealedAt: nowIso(), attested: isClockPlausible() };
    await db.dayPlans.put(row);
    await enqueue('dayPlan.save', row, ['ritualSealedAt', 'attested']);
  });
}

export async function saveWeekReward(weekKey: string, reward: string): Promise<void> {
  const existente = await db.weekPlans.where('weekKey').equals(weekKey).first();
  const row: WeekPlan = {
    id: existente?.id ?? weekPlanId(getUserId(), weekKey),
    weekKey,
    reward: reward.trim().slice(0, 120),
    deletedAt: null,
  };
  await db.transaction('rw', db.weekPlans, db.outbox, db.meta, async () => {
    await db.weekPlans.put(row);
    await enqueue('weekPlan.save', row, ['reward']);
  });
}

// ===========================================================================
// AJUSTES
// ===========================================================================

/**
 * Los ajustes **no son una colección replicada** y por eso no van por la outbox: no
 * tienen lápida, no tienen HLC por campo, y no hay dos dispositivos compitiendo de
 * forma interesante por el mismo campo —el último que los cambia es el que el
 * usuario está mirando—. Tienen su propia ruta (PRD 12.1: solo tiene ruta propia lo
 * que no es sincronizable).
 *
 * Se marcan como sucios y el motor los sube cuando pueda. Si no hay red, el cambio
 * ya está aplicado aquí, que es donde el usuario lo ve.
 */
export async function updateSettings(patch: Partial<Omit<Settings, 'id'>>): Promise<void> {
  await db.transaction('rw', db.settings, db.meta, async () => {
    const current = await db.settings.get('unica');
    if (!current) return;
    await db.settings.put({ ...current, ...patch });
    // Un sello distinto en cada cambio: la subida sabe si hubo otro mientras tanto.
    await db.meta.put({ key: 'ajustesSucios', value: `${Date.now()}-${Math.random()}` });
  });
}

// ===========================================================================
// TEMPORADAS SELLADAS
// ===========================================================================

/**
 * Sella una temporada cerrada y la encola (`season.seal`). Se sella UNA vez: si
 * ya está —la selló este u otro dispositivo y llegó por pull— no se toca, y
 * devuelve `false`. El id es determinista por usuario y clave, así que dos
 * dispositivos que la sellen sin red convergen en la misma fila.
 */
export async function sealSeasonRow(row: SealedSeasonRow): Promise<boolean> {
  return db.transaction('rw', db.seasons, db.outbox, db.meta, async () => {
    if (await db.seasons.get(row.key)) return false;
    await db.seasons.put(row);
    await enqueue('season.seal', { ...row, id: sealedSeasonId(getUserId(), row.key) }, [
      'seasonKey',
      'label',
      'fromDate',
      'toDate',
      'points',
      'ligaKey',
      'ligaName',
      'milestones',
      'sealedAt',
    ]);
    return true;
  });
}

// ===========================================================================
// AWARDS · se derivan, no se guardan
// ===========================================================================

/**
 * Los eventos de progresión no se almacenan: se **derivan** del historial cada
 * vez. Es la misma decisión que las rachas y por el mismo motivo (PRD 6.11): un
 * contador replicado es la peor estructura posible bajo sincronización, porque
 * dos dispositivos lo incrementan sin conexión y se pierde uno.
 *
 * Sobre unos miles de filas esto es instantáneo, y a cambio el conflicto
 * desaparece de raíz: no hay nada que reconciliar.
 */
export { awardForFocus, awardForHabit, awardForJournal, awardForMood };
